import { CircleNotchIcon, UsersThreeIcon } from '@phosphor-icons/react';
import type { EventAudienceMetadataCardProps } from '@/lib/types/events.types';
import { getEventAudienceRoleLabel } from '@/lib/utils/event-audience.utils';
import { formatGroupStatus, getGroupRoleTone } from '@/lib/utils/groups.utils';
import { cn } from '@/lib/utils';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';

export function EventAudienceMetadataCard({
  metadata,
  isLoading,
  error,
}: EventAudienceMetadataCardProps) {
  useRoleDirectory();
  if (error) {
    return (
      <section
        data-testid="event-audience-metadata-card"
        className="mt-4 rounded-lg border border-red-400/40 bg-red-500/10 p-4 text-red-100"
      >
        <h2 className="text-base font-semibold">
          Audience metadata unavailable
        </h2>
        <p className="mt-1 text-sm text-red-100/80">{error.message}</p>
      </section>
    );
  }

  if (isLoading || !metadata) {
    return (
      <section
        data-testid="event-audience-metadata-card"
        className="mt-4 rounded-lg bg-slate-700 p-4 text-slate-200"
      >
        <div className="flex items-center gap-2">
          <CircleNotchIcon size={20} weight="bold" className="animate-spin" />
          <h2 className="text-base font-semibold">Loading audience metadata</h2>
        </div>
      </section>
    );
  }

  const activeGroups = metadata.groupTargets.filter(
    (group) => !group.archived_at
  );
  const archivedGroups = metadata.groupTargets.filter(
    (group) => group.archived_at
  );

  return (
    <section
      data-testid="event-audience-metadata-card"
      className="mt-4 rounded-lg bg-slate-700 p-4 text-slate-200"
      aria-label="Event audience metadata"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold">Audience metadata</h2>
          <p className="mt-1 text-sm text-slate-400">
            {metadata.resolvedMemberCount} deduplicated members
          </p>
        </div>
        <UsersThreeIcon
          size={24}
          weight="regular"
          className="text-purple-300"
        />
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Role targets
          </h3>
          {metadata.roleTargets.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {metadata.roleTargets.map((role) => (
                <span
                  key={role}
                  className={cn(
                    'rounded-[6px] border px-3 py-1 text-sm',
                    getGroupRoleTone(role).pillClassName
                  )}
                >
                  {getEventAudienceRoleLabel(role)}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-500">No role-wide targets</p>
          )}
        </div>

        <AudienceSection
          label="Group targets"
          values={activeGroups.map(
            (group) => `${group.display_name} (${group.member_count} members)`
          )}
          emptyLabel="No active group targets"
        />

        {archivedGroups.length > 0 && (
          <AudienceSection
            label="Archived targets"
            values={archivedGroups.map(
              (group) =>
                `${group.display_name} (${formatGroupStatus(group.archived_at)})`
            )}
            emptyLabel="No archived group targets"
          />
        )}
      </div>
    </section>
  );
}

function AudienceSection({
  label,
  values,
  emptyLabel,
}: {
  label: string;
  values: string[];
  emptyLabel: string;
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </h3>
      {values.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="rounded-[6px] border border-purple-300/30 bg-purple-300/10 px-3 py-1 text-sm text-purple-100"
            >
              {value}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-1 text-sm text-slate-500">{emptyLabel}</p>
      )}
    </div>
  );
}
